import { Check } from "lucide-react";
import { Reveal } from "@/components/shared/Reveal";
import { SectionHeading } from "@/components/shared/SectionHeading";
import { CtaButton } from "@/components/shared/CtaButton";
import { useLanguage } from "@/context/LanguageContext";
import { CONTENT } from "@/i18n/content";
import { PRICING } from "@/lib/config";

function Feature({ children, gold }) {
  return (
    <li className="flex items-start gap-2.5 text-sm">
      <Check className={`mt-0.5 h-4 w-4 shrink-0 ${gold ? "text-gold" : "text-emerald-600"}`} strokeWidth={3} />
      <span className={gold ? "text-cream/85" : "text-navy/75"}>{children}</span>
    </li>
  );
}

export function Pricing() {
  const { lang } = useLanguage();
  const t = CONTENT[lang].pricing;

  return (
    <section id="pricing" className="scroll-mt-20 bg-cream py-20 sm:py-28" data-testid="pricing">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <SectionHeading eyebrow={t.eyebrow} title={t.title} sub={t.sub} />

        <div className="mx-auto mt-14 grid max-w-4xl items-stretch gap-6 lg:grid-cols-2">
          {/* Monthly — primary, active */}
          <Reveal>
            <div className="relative flex h-full flex-col overflow-hidden rounded-3xl border-2 border-gold bg-navy p-8 shadow-[0_30px_60px_-24px_rgba(15,27,61,0.5)]">
              <span className="absolute right-6 top-6 inline-flex items-center rounded-full bg-gold px-3 py-1 text-[11px] font-bold uppercase tracking-wide text-navy">
                {t.monthly.badge}
              </span>
              <h3 className="font-heading text-xl font-semibold text-cream">{t.monthly.name}</h3>
              <div className="mt-4 flex items-baseline gap-1">
                <span className="font-heading text-4xl font-bold text-cream">{PRICING.monthly.amount}</span>
                <span className="text-sm text-cream/55">{PRICING.monthly.period}</span>
              </div>
              <ul className="mt-6 flex-1 space-y-3">
                {t.monthly.features.map((f) => (
                  <Feature key={f} gold>{f}</Feature>
                ))}
              </ul>
              <CtaButton variant="gold" testId="pricing-monthly-cta" className="mt-8 w-full">
                {t.cta}
              </CtaButton>
            </div>
          </Reveal>

          {/* Annual — visible, launching soon */}
          <Reveal delay={120}>
            <div className="relative flex h-full flex-col rounded-3xl border border-navy/10 bg-white p-8">
              <span className="absolute right-6 top-6 inline-flex items-center rounded-full bg-navy/[0.06] px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-navy/55">
                {t.comingSoon}
              </span>
              <h3 className="font-heading text-xl font-semibold text-navy">{t.annual.name}</h3>
              <div className="mt-4 flex items-baseline gap-2">
                <span className="font-heading text-lg text-navy/40 line-through">{PRICING.annual.original}</span>
              </div>
              <div className="flex items-baseline gap-1">
                <span className="font-heading text-4xl font-bold text-navy">{PRICING.annual.amount}</span>
                <span className="text-sm text-navy/55">{PRICING.annual.period}</span>
              </div>
              <span className="mt-3 inline-flex w-fit items-center rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700 ring-1 ring-emerald-100">
                {t.annual.saveLabel} {PRICING.annual.save} {t.perYear}
              </span>
              <ul className="mt-6 flex-1 space-y-3">
                {t.annual.features.map((f) => (
                  <Feature key={f}>{f}</Feature>
                ))}
              </ul>
              <button
                type="button"
                disabled
                data-testid="pricing-annual-cta"
                className="mt-8 w-full cursor-not-allowed rounded-full border border-navy/15 bg-navy/[0.04] px-7 py-3.5 text-sm font-medium text-navy/45"
              >
                {t.comingSoon}
              </button>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
